import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
    Autocomplete,
    Box,
    Button,
    Chip,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Pagination,
    Paper,
    Select,
    Stack,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import EventNoteRoundedIcon from '@mui/icons-material/EventNoteRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import FilterAltOffOutlinedIcon from '@mui/icons-material/FilterAltOffOutlined';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import { listEncounters, createEncounter, listAssignablePhysicians } from '../../../api/encounter';
import { apiGet } from '../../../api/client';
import { usePermissions } from '../../../hooks/usePermissions';

const NAVY = '#052F5D';
const PAGE_SIZE = 25;

function formatDate(d) {
    if (!d) return '—';
    const date = new Date(d);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function patientLabel(p) {
    if (!p) return '';
    const name = `${p.fName || ''} ${p.lName || ''}`.trim() || 'Unnamed';
    return p.mrn ? `${name} · MRN ${p.mrn}` : name;
}

export default function EncountersList() {
    const navigate = useNavigate();
    const theme = useTheme();
    const isXs = useMediaQuery(theme.breakpoints.down('sm'));
    const { role: roleParam } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const { canWrite } = usePermissions();
    const canCreate = canWrite('encounters');

    // "New encounter" modal — picks a patient and creates a draft, then
    // navigates straight into the editor. Lives here so users can start
    // an encounter without first navigating into a patient profile.
    const [createOpen, setCreateOpen] = useState(false);
    const [createPatient, setCreatePatient] = useState(null);
    const [createSearchInput, setCreateSearchInput] = useState('');
    const [createOptions, setCreateOptions] = useState([]);
    const [createOptionsLoading, setCreateOptionsLoading] = useState(false);
    const [creating, setCreating] = useState(false);
    const createSearchSeq = useRef(0);
    // Physician roster + the chosen attending for the new encounter. Empty
    // means "auto" — a physician creating the visit owns it; otherwise the
    // visit starts unassigned.
    const [physicians, setPhysicians] = useState([]);
    const [assignPhysicianId, setAssignPhysicianId] = useState('');

    const patientId = searchParams.get('patientId') || '';
    const status = searchParams.get('status') || '';
    const from = searchParams.get('from') || '';
    const to = searchParams.get('to') || '';
    const pageParam = parseInt(searchParams.get('page') || '1', 10);
    const page = Number.isFinite(pageParam) && pageParam > 0 ? pageParam : 1;

    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);

    // Patient picker state — owned locally so it doesn't fight with the
    // shared patientProfile redux slice used by the patient list page.
    const [selectedPatient, setSelectedPatient] = useState(null);
    const [patientOptions, setPatientOptions] = useState([]);
    const [patientSearchInput, setPatientSearchInput] = useState('');
    const [patientSearchLoading, setPatientSearchLoading] = useState(false);
    const patientSearchSeq = useRef(0);

    const updateParams = useCallback((next) => {
        const merged = new URLSearchParams(searchParams);
        for (const [k, v] of Object.entries(next)) {
            if (v === '' || v === null || v === undefined) merged.delete(k);
            else merged.set(k, v);
        }
        setSearchParams(merged, { replace: false });
    }, [searchParams, setSearchParams]);

    // Pulling the currentBranch the same way the api client does keeps
    // patient search consistent with how encounters are scoped server-side.
    const currentBranchId = useMemo(() => {
        try {
            const raw = localStorage.getItem('currentBranch');
            return raw ? (JSON.parse(raw)?.id || null) : null;
        } catch {
            return null;
        }
    }, []);

    // Load encounters whenever filters or page change
    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        const params = {
            limit: PAGE_SIZE,
            skip: (page - 1) * PAGE_SIZE,
        };
        if (patientId) params.patientId = patientId;
        if (status) params.status = status;
        if (from) params.from = from;
        if (to) params.to = to;
        listEncounters(params)
            .then((res) => {
                if (cancelled) return;
                setItems(res?.data || []);
                setTotal(res?.total || 0);
            })
            .catch(() => {
                if (cancelled) return;
                setItems([]);
                setTotal(0);
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [patientId, status, from, to, page]);

    // If the URL has a patientId on first load but no selected option,
    // resolve it once so the autocomplete shows the patient's name
    // instead of an empty input.
    useEffect(() => {
        if (!patientId || selectedPatient?._id === patientId) return;
        apiGet(`api/get-patient-data/basicInformation/${patientId}`)
            .then((r) => {
                const p = r?.patientData || r?.data || r;
                if (p && p._id) setSelectedPatient(p);
            })
            .catch(() => { /* leave picker blank */ });
    }, [patientId, selectedPatient]);

    // Debounced patient search — race-cancel intermediate responses.
    useEffect(() => {
        if (!currentBranchId) return undefined;
        const handle = setTimeout(() => {
            const seq = ++patientSearchSeq.current;
            setPatientSearchLoading(true);
            apiGet(`api/get-patient-profile/${currentBranchId}`, {
                limit: 20,
                skip: 0,
                search: patientSearchInput || '',
                searchType: 'fullName',
            })
                .then((res) => {
                    if (seq !== patientSearchSeq.current) return;
                    setPatientOptions(res?.data || []);
                })
                .catch(() => {
                    if (seq !== patientSearchSeq.current) return;
                    setPatientOptions([]);
                })
                .finally(() => {
                    if (seq === patientSearchSeq.current) setPatientSearchLoading(false);
                });
        }, 250);
        return () => clearTimeout(handle);
    }, [patientSearchInput, currentBranchId]);

    const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const hasFilters = Boolean(patientId || status || from || to);

    const clearFilters = () => {
        setSelectedPatient(null);
        setPatientSearchInput('');
        setSearchParams(new URLSearchParams(), { replace: false });
    };

    const openEncounter = (e) => {
        const pid = e.patient?._id || e.patientId;
        if (!pid) return;
        navigate(`/${roleParam || 'admin'}/patients/${pid}/encounters/${e._id}`);
    };

    // Patient search inside the create dialog — separate from the filter
    // bar's picker so opening the dialog doesn't blow away the user's
    // current filter selection.
    useEffect(() => {
        if (!createOpen || !currentBranchId) return undefined;
        const handle = setTimeout(() => {
            const seq = ++createSearchSeq.current;
            setCreateOptionsLoading(true);
            apiGet(`api/get-patient-profile/${currentBranchId}`, {
                limit: 20,
                skip: 0,
                search: createSearchInput || '',
                searchType: 'fullName',
            })
                .then((res) => {
                    if (seq !== createSearchSeq.current) return;
                    setCreateOptions(res?.data || []);
                })
                .catch(() => {
                    if (seq !== createSearchSeq.current) return;
                    setCreateOptions([]);
                })
                .finally(() => {
                    if (seq === createSearchSeq.current) setCreateOptionsLoading(false);
                });
        }, 250);
        return () => clearTimeout(handle);
    }, [createSearchInput, currentBranchId, createOpen]);

    // Physician roster for the assignment picker (encounters:write only).
    useEffect(() => {
        if (!canCreate) return undefined;
        let cancelled = false;
        listAssignablePhysicians()
            .then((r) => { if (!cancelled) setPhysicians(r.data || []); })
            .catch(() => { /* best-effort — picker just stays empty */ });
        return () => { cancelled = true; };
    }, [canCreate]);

    const openCreateDialog = () => {
        setCreatePatient(null);
        setCreateSearchInput('');
        setCreateOptions([]);
        setAssignPhysicianId('');
        setCreateOpen(true);
    };

    const handleCreate = async () => {
        if (!createPatient?._id) return;
        try {
            setCreating(true);
            const r = await createEncounter(createPatient._id, undefined, assignPhysicianId || undefined);
            const id = r?.data?._id;
            if (id) {
                navigate(`/${roleParam || 'admin'}/patients/${createPatient._id}/encounters/${id}`);
            }
        } finally {
            setCreating(false);
            setCreateOpen(false);
        }
    };

    return (
        <Container maxWidth={false} sx={{ maxWidth: 1320, px: { xs: 1.5, md: 3 }, py: { xs: 1.5, md: 2 } }}>
            <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, mb: 0.5 }}>
                <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: '1.5rem', color: NAVY }}>
                        Encounters
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Browse and filter every encounter across the clinic.
                    </Typography>
                </Box>
                {/* Greyed out (not hidden) for read-only roles so the
                    capability is discoverable and the tooltip explains why. */}
                <Tooltip title={canCreate ? '' : 'You have read-only access to encounters.'} arrow>
                    <span>
                        <Button
                            onClick={openCreateDialog}
                            disabled={!canCreate}
                            variant="contained"
                            startIcon={<AddRoundedIcon />}
                            sx={{
                                bgcolor: NAVY,
                                boxShadow: 'none',
                                textTransform: 'none',
                                fontWeight: 600,
                                whiteSpace: 'nowrap',
                                '&:hover': { bgcolor: '#03244a', boxShadow: 'none' },
                            }}
                        >
                            New encounter
                        </Button>
                    </span>
                </Tooltip>
            </Box>
            <Box sx={{ mb: 2 }} />

            {/* Filter bar */}
            <Paper
                variant="outlined"
                sx={{
                    p: { xs: 1.5, md: 2 },
                    mb: 2,
                    borderRadius: 2,
                    borderColor: 'rgba(5,47,93,0.10)',
                    bgcolor: '#fff',
                }}
            >
                <Grid container spacing={1.5} alignItems="center">
                    <Grid item xs={12} md={5}>
                        <Autocomplete
                            value={selectedPatient}
                            onChange={(_, val) => {
                                setSelectedPatient(val);
                                updateParams({ patientId: val?._id || '', page: '' });
                            }}
                            inputValue={patientSearchInput}
                            onInputChange={(_, val, reason) => {
                                if (reason !== 'reset') setPatientSearchInput(val);
                            }}
                            options={patientOptions}
                            getOptionLabel={patientLabel}
                            isOptionEqualToValue={(opt, val) => opt?._id === val?._id}
                            loading={patientSearchLoading}
                            filterOptions={(opts) => opts}
                            noOptionsText={patientSearchInput ? 'No patients match' : 'Start typing a name or MRN'}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    size="small"
                                    label="Patient"
                                    placeholder="Search by name or MRN"
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {patientSearchLoading ? <CircularProgress size={14} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={6} md={2}>
                        <FormControl size="small" fullWidth>
                            <InputLabel id="enc-status-label">Status</InputLabel>
                            <Select
                                labelId="enc-status-label"
                                label="Status"
                                value={status}
                                onChange={(e) => updateParams({ status: e.target.value, page: '' })}
                            >
                                <MenuItem value="">All</MenuItem>
                                <MenuItem value="draft">Draft</MenuItem>
                                <MenuItem value="finalized">Finalized</MenuItem>
                                <MenuItem value="signed">Signed</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={6} md={2}>
                        <TextField
                            size="small"
                            fullWidth
                            type="date"
                            label="From"
                            InputLabelProps={{ shrink: true }}
                            value={from}
                            onChange={(e) => updateParams({ from: e.target.value, page: '' })}
                        />
                    </Grid>
                    <Grid item xs={6} md={2}>
                        <TextField
                            size="small"
                            fullWidth
                            type="date"
                            label="To"
                            InputLabelProps={{ shrink: true }}
                            value={to}
                            onChange={(e) => updateParams({ to: e.target.value, page: '' })}
                        />
                    </Grid>
                    <Grid item xs={6} md={1}>
                        <Button
                            onClick={clearFilters}
                            disabled={!hasFilters}
                            startIcon={<FilterAltOffOutlinedIcon />}
                            sx={{ textTransform: 'none', color: NAVY, fontWeight: 600, whiteSpace: 'nowrap' }}
                            fullWidth
                        >
                            Clear
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {/* Result list */}
            <Paper
                elevation={0}
                sx={{
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 2,
                    p: { xs: 1, md: 1.5 },
                    bgcolor: '#fff',
                }}
            >
                {loading ? (
                    <Box sx={{ p: 6, textAlign: 'center' }}>
                        <CircularProgress size={26} sx={{ color: NAVY }} />
                    </Box>
                ) : items.length === 0 ? (
                    <Box
                        sx={{
                            p: 5,
                            textAlign: 'center',
                            border: '1px dashed rgba(5,47,93,0.15)',
                            borderRadius: 2,
                            bgcolor: 'rgba(5,47,93,0.025)',
                        }}
                    >
                        <EventNoteRoundedIcon sx={{ fontSize: 36, color: NAVY, opacity: 0.4 }} />
                        <Typography sx={{ mt: 1, color: NAVY, fontWeight: 600 }}>
                            No encounters {hasFilters ? 'match these filters' : 'recorded yet'}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                            {hasFilters
                                ? 'Try clearing a filter to widen the search.'
                                : 'New encounters created from a patient profile will appear here.'}
                        </Typography>
                    </Box>
                ) : (
                    <Stack divider={<Divider sx={{ borderColor: 'rgba(5,47,93,0.06)' }} />}>
                        {items.map((e) => {
                            const isFin = e.status === 'finalized' || e.status === 'signed';
                            const primary = (e.diagnoses || []).find((d) => d.primary) || (e.diagnoses || [])[0];
                            const dxCount = (e.diagnoses || []).length;
                            // A signed visit reopened for correction reads as
                            // "Amending", not "Finalized" — the record is
                            // unlocked and the row is actionable again.
                            // `amendmentCount` marks visits already corrected.
                            const amendLabel = e.amendmentOpen
                                ? 'Amending'
                                : e.amendmentCount ? 'Amended' : null;
                            const amber = Boolean(e.amendmentOpen);
                            // Rendered twice: inline next to the name on phones,
                            // in the trailing action group on desktop.
                            const statusChip = (
                                <Chip
                                    size="small"
                                    icon={isFin ? <LockOutlinedIcon sx={{ fontSize: 13 }} /> : <EditOutlinedIcon sx={{ fontSize: 13 }} />}
                                    label={amendLabel || (e.status ? e.status.charAt(0).toUpperCase() + e.status.slice(1) : '—')}
                                    sx={{
                                        bgcolor: amber ? '#FFF6E5' : isFin ? 'rgba(46,125,50,0.10)' : 'rgba(5,47,93,0.08)',
                                        color: amber ? '#5C3A00' : isFin ? 'success.dark' : NAVY,
                                        border: '1px solid',
                                        borderColor: amber ? '#F5C26B' : isFin ? 'rgba(46,125,50,0.25)' : 'rgba(5,47,93,0.15)',
                                        fontWeight: 600,
                                        '.MuiChip-icon': { color: 'inherit' },
                                    }}
                                />
                            );
                            return (
                                <Box
                                    key={e._id}
                                    onClick={() => openEncounter(e)}
                                    sx={{
                                        p: { xs: 1.5, md: 2 },
                                        borderRadius: 1.5,
                                        cursor: 'pointer',
                                        display: 'flex',
                                        // Phones get a stacked card; md+ keeps the original
                                        // single-line row untouched.
                                        alignItems: { xs: 'stretch', md: 'center' },
                                        gap: { xs: 1.5, md: 2 },
                                        transition: 'background-color 140ms ease',
                                        '&:hover': { bgcolor: 'rgba(5,47,93,0.035)' },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 6, alignSelf: 'stretch', borderRadius: 999,
                                            flexShrink: 0,
                                            bgcolor: isFin ? 'success.main' : 'rgba(5,47,93,0.25)',
                                        }}
                                    />
                                    <Box
                                        sx={{
                                            flex: 1,
                                            minWidth: 0,
                                            display: 'flex',
                                            flexDirection: { xs: 'column', md: 'row' },
                                            alignItems: { xs: 'stretch', md: 'center' },
                                            gap: { xs: 0.5, md: 2 },
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                minWidth: { md: 180 },
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                gap: 1,
                                            }}
                                        >
                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography sx={{ fontWeight: 600, color: NAVY, fontSize: 14 }} noWrap>
                                                    {e.patient
                                                        ? `${e.patient.fName || ''} ${e.patient.lName || ''}`.trim() || 'Unknown patient'
                                                        : 'Unknown patient'}
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                    {e.patient?.mrn ? `MRN ${e.patient.mrn}` : '—'}
                                                    {/* On phones the standalone date column is hidden,
                                                        so the date joins the MRN caption instead. */}
                                                    <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>
                                                        {' · '}{formatDate(e.visitDate)}
                                                    </Box>
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: { xs: 'flex', md: 'none' }, flexShrink: 0 }}>
                                                {statusChip}
                                            </Box>
                                        </Box>
                                        <Box sx={{ minWidth: 110, display: { xs: 'none', md: 'block' } }}>
                                            <Typography sx={{ fontWeight: 500, color: 'text.primary', fontSize: 14 }}>
                                                {formatDate(e.visitDate)}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ flex: { md: 1 }, minWidth: 0 }}>
                                            <Typography
                                                sx={{ fontSize: 14, color: 'text.primary', whiteSpace: { xs: 'normal', md: 'nowrap' } }}
                                                noWrap
                                            >
                                                {e.chiefComplaint || (
                                                    <Box component="span" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
                                                        No chief complaint recorded
                                                    </Box>
                                                )}
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                sx={{ color: 'text.secondary', whiteSpace: { xs: 'normal', md: 'nowrap' } }}
                                                noWrap
                                            >
                                                {primary
                                                    ? <>Dx: {(primary.code || primary.icd10Code) ? <><strong>{primary.code || primary.icd10Code}</strong>{' '}</> : null}{primary.description} {dxCount > 1 ? `· +${dxCount - 1} more` : ''}</>
                                                    : 'No diagnosis yet'}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1, flexShrink: 0 }}>
                                        {e.assignedPhysician ? (
                                            <Tooltip title="Assigned physician" arrow>
                                                <Typography variant="caption" sx={{ color: 'text.secondary', maxWidth: 150, fontWeight: 500 }} noWrap>
                                                    {e.assignedPhysician.name}
                                                </Typography>
                                            </Tooltip>
                                        ) : !isFin ? (
                                            // Only a draft with no physician is a gap worth flagging.
                                            // A finalized visit is unassigned by design (it leaves the
                                            // physician's worklist), so don't show the warning chip.
                                            <Chip
                                                size="small"
                                                label="Unassigned"
                                                sx={{ bgcolor: '#FFF6E5', color: '#5C3A00', border: '1px solid #F5C26B', fontWeight: 600, height: 22 }}
                                            />
                                        ) : null}
                                        {statusChip}
                                        <Tooltip title="Open" arrow>
                                            <IconButton size="small" sx={{ color: 'text.disabled' }}>
                                                <ChevronRightRoundedIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </Box>
                            );
                        })}
                    </Stack>
                )}
            </Paper>

            {pageCount > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Pagination
                        count={pageCount}
                        page={page}
                        onChange={(_, p) => updateParams({ page: p === 1 ? '' : String(p) })}
                        color="primary"
                        shape="rounded"
                        // Fewer page buttons on phones so the control never overflows
                        siblingCount={isXs ? 0 : 1}
                        size={isXs ? 'small' : 'medium'}
                    />
                </Box>
            )}

            {/* fullScreen on phones: a small centered dialog over the keyboard
                leaves the autocomplete dropdown almost no room. */}
            <Dialog open={createOpen} onClose={() => !creating && setCreateOpen(false)} maxWidth="sm" fullWidth fullScreen={isXs}>
                <DialogTitle sx={{ color: NAVY, fontWeight: 600 }}>Start a new encounter</DialogTitle>
                <DialogContent dividers sx={{ borderColor: 'rgba(0,0,0,0.06)' }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                        Pick the patient and we'll open a draft encounter for them.
                    </Typography>
                    <Autocomplete
                        autoFocus
                        value={createPatient}
                        onChange={(_, val) => setCreatePatient(val)}
                        inputValue={createSearchInput}
                        onInputChange={(_, val, reason) => {
                            if (reason !== 'reset') setCreateSearchInput(val);
                        }}
                        options={createOptions}
                        getOptionLabel={patientLabel}
                        isOptionEqualToValue={(opt, val) => opt?._id === val?._id}
                        loading={createOptionsLoading}
                        filterOptions={(opts) => opts}
                        noOptionsText={createSearchInput ? 'No patients match' : 'Start typing a name or MRN'}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Patient"
                                placeholder="Search by name or MRN"
                                autoFocus
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {createOptionsLoading ? <CircularProgress size={14} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                    />

                    <TextField
                        select
                        fullWidth
                        size="small"
                        sx={{ mt: 2 }}
                        label="Assign to physician"
                        value={assignPhysicianId}
                        onChange={(e) => setAssignPhysicianId(e.target.value)}
                        SelectProps={{ displayEmpty: true }}
                        InputLabelProps={{ shrink: true }}
                        helperText="Physicians only see visits assigned to them. Leave blank to assign yourself (if you're a physician) or set it later."
                    >
                        <MenuItem value=""><em>Unassigned / assign later</em></MenuItem>
                        {physicians.map((p) => (
                            <MenuItem key={p._id} value={p._id}>{p.name}</MenuItem>
                        ))}
                    </TextField>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={() => setCreateOpen(false)} disabled={creating} sx={{ color: 'text.secondary' }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleCreate}
                        disabled={!createPatient?._id || creating}
                        variant="contained"
                        sx={{
                            bgcolor: NAVY,
                            boxShadow: 'none',
                            textTransform: 'none',
                            fontWeight: 600,
                            '&:hover': { bgcolor: '#03244a', boxShadow: 'none' },
                        }}
                    >
                        {creating ? 'Creating…' : 'Create encounter'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}
